<template>
  <div class="deliver-to-modal modal named-carts-add-consumer" role="dialog">
    <div class="named-carts-add-consumer__modal-box modal-dialog">
      <div class="named-carts-add-consumer__modal-header">
        <div class="named-carts-add-consumer__modal-title">Create A New Customer</div>
        <button
          type="button"
          aria-hidden="true"
          aria-label="Close"
          class="named-carts-add-consumer__modal-button-close"
          @click.prevent="handleClose"
        >
          <UseSvg icon="modal-close-big" />
        </button>
      </div>
      <div class="named-carts-add-consumer__modal-body">
        <form action="#" class="named-carts-add-consumer__form" @submit.prevent="handleSubmit">
          <ErrorNotification
            v-if="error"
            :notification="{ text: error }"
            class="named-carts-add-consumer__row named-carts-add-consumer__col"
          />
          <div class="named-carts-add-consumer__row">
            <NamedCartsField
              ref="firstName"
              :value="consumer.first_name"
              :valid="isFirstNameValid"
              :hint="isFirstNameValid ? '' : 'This field is required'"
              :mark-required="true"
              label="First Name"
              placeholder="First Name"
              class="named-carts-add-consumer__col"
              @input="consumer.first_name = $event.target.value"
            />

            <NamedCartsField
              :value="consumer.last_name"
              :valid="isLastNameValid"
              :hint="isLastNameValid ? '' : 'This field is required'"
              :mark-required="true"
              label="Last Name"
              placeholder="Last Name"
              class="named-carts-add-consumer__col"
              @input="consumer.last_name = $event.target.value"
            />
          </div>
          <div class="named-carts-add-consumer__row">
            <NamedCartsField
              :value="consumer.company_name"
              label="Company"
              placeholder="Company"
              class="named-carts-add-consumer__col"
              @input="consumer.company_name = $event.target.value"
            />
          </div>
          <div class="named-carts-add-consumer__row">
            <NamedCartsField
              :value="consumer.email"
              :valid="isEmailValid"
              :hint="isEmailValid ? '' : 'Please enter valid email'"
              label="Email"
              placeholder="Email"
              class="named-carts-add-consumer__col"
              @input="consumer.email = $event.target.value"
            />
          </div>
          <div class="named-carts-add-consumer__row">
            <NamedCartsField
              :value="consumer.phone"
              label="Phone Number"
              placeholder="Phone Number"
              class="named-carts-add-consumer__col"
              @input="consumer.phone = $event.target.value"
            />
          </div>
          <div class="named-carts-add-consumer__row">
            <NamedCartsField
              :value="consumer.notes"
              :is-textarea="true"
              label="Notes"
              placeholder="Notes"
              class="named-carts-add-consumer__col"
              @input="consumer.notes = $event.target.value"
            />
          </div>

          <ButtonBox
            :disabled="savedCartsIsCustomerCreating"
            type="submit"
            :style="'primary'"
            class="named-carts-add-consumer__dialog-button"
          >
            {{ savedCartsIsCustomerCreating ? "Saving..." : "Save" }}
          </ButtonBox>

          <ButtonBox type="button" :style="'outline'" @click.prevent="handleClose">
            Cancel
          </ButtonBox>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import UseSvg from "../UseSvg.vue";
import NamedCartsField from "./NamedCartsField.vue";
import ErrorNotification from "../Notifications/ErrorNotification.vue";
import ButtonBox from "../../button-box/ButtonBox.vue";

const DEFAULT_CONSUMER = Object.freeze({
  first_name: "",
  last_name: "",
  email: "",
  phone: ""
});

export default {
  name: "NamedCartsAddConsumer",
  components: {
    ButtonBox,
    UseSvg,
    NamedCartsField,
    ErrorNotification
  },
  emits: ["consumer-created"],
  data() {
    /* eslint-disable no-useless-escape */
    return {
      consumer: { ...DEFAULT_CONSUMER },
      loading: false,
      emailRegex:
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/,
      touched: false,
      error: null
    };
  },
  computed: {
    ...mapGetters(["savedCartsIsCustomerCreating"]),
    isValid() {
      return this.consumer.first_name && this.consumer.last_name && this.isEmailValid;
    },
    isFirstNameValid() {
      return !this.touched || Boolean(this.consumer.first_name);
    },
    isLastNameValid() {
      return !this.touched || Boolean(this.consumer.last_name);
    },
    isEmailValid() {
      return (
        !this.touched ||
        !this.consumer.email ||
        (Boolean(this.consumer.email) && this.validEmailFormat)
      );
    },
    validEmailFormat() {
      return this.emailRegex.test(this.consumer.email);
    }
  },
  methods: {
    ...mapActions(["createCartCustomer"]),
    open() {
      $(this.$el).modal("show");
      this.$refs.firstName.focus();
    },
    handleClose() {
      $(this.$el).modal("hide");
      this.touched = false;
      this.consumer = { ...DEFAULT_CONSUMER };
    },
    async handleSubmit() {
      this.touched = true;

      if (this.isValid) {
        try {
          this.error = null;
          const { errors } = await this.createCartCustomer(this.consumer);
          if (errors) {
            this.error = Object.keys(errors).reduce((acct, error, index) => {
              const message = errors[error].join(", ");
              return `${acct}${index ? "\n" : ""}${error} ${message}`;
            }, "");
          } else {
            this.$emit("consumer-created");
            this.handleClose();
          }
        } catch (error) {
          this.error = error.message;
        }
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";
@import "frontend/init/jiffy/modal.scss";

.named-carts-add-consumer {
  @include jiffy-modal;

  &__modal-box {
    position: absolute;
    top: initial;
    bottom: 0;
    width: 100%;
    padding: 0;
    transform: none;

    @include with-tablet {
      position: relative;
      top: 50%;
    }
  }

  &__dialog-button {
    margin-top: 24px;
    margin-bottom: 10px;

    @include with-tablet {
      margin-top: 40px;
      margin-bottom: 20px;
    }
  }

  &__form {
    display: flex;
    flex-direction: column;
    text-align: left;
  }

  &__row {
    display: flex;

    & + & {
      margin-top: 24px;
    }
  }

  &__col {
    flex: 1;

    & + & {
      margin-left: 16px;
    }
  }
}
</style>
